import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleText, X, PaperPlaneTilt, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";

export default function ConciergeWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [bookingLink, setBookingLink] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  // Start a session the first time user opens the widget
  useEffect(() => {
    if (open && !sessionId) {
      apiClient.post("/concierge/session", {}).then(({ data }) => {
        setSessionId(data.session_id);
        setMessages([{ role: "assistant", text: data.greeting }]);
      }).catch(() => {
        setMessages([{ role: "assistant", text: "Our AI concierge is briefly unavailable. Please use the Book Trip form instead." }]);
      });
    }
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
    if (open && inputRef.current) inputRef.current.focus();
  }, [messages, open]);

  const send = async () => {
    const txt = draft.trim();
    if (!txt || sending || !sessionId) return;
    setMessages((m) => [...m, { role: "user", text: txt }]);
    setDraft("");
    setSending(true);
    try {
      const { data } = await apiClient.post("/concierge/chat", { session_id: sessionId, message: txt });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.booked && data.booking_id) {
        setBookingId(data.booking_id);
        setBookingLink(data.booking_link || `/t/${data.booking_id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong — try again or use the Book Trip page.";
      setMessages((m) => [...m, { role: "assistant", text: String(detail) }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ opacity: 0, scale: 0.7, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 40 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            onClick={() => setOpen(true)}
            data-testid="concierge-launcher"
            className="fixed bottom-6 right-6 z-[60] inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-brandblue to-ova text-white pl-4 pr-5 py-3 shadow-[0_20px_60px_-15px_rgba(58,123,213,0.55)] hover:shadow-[0_25px_70px_-10px_rgba(58,123,213,0.7)] transition-shadow"
          >
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/25">
              <Sparkle size={18} weight="fill" className="text-white" />
              <span className="absolute inset-0 rounded-full bg-white/25 live-dot" />
            </span>
            <span className="text-left leading-tight">
              <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold opacity-80">Ava · AI concierge</span>
              <span className="block font-heading text-sm font-bold">Book a trip in 60s</span>
            </span>
            <ArrowRight size={16} weight="bold" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            data-testid="concierge-panel"
            className="fixed bottom-6 right-6 z-[60] w-[min(400px,calc(100vw-24px))] h-[min(620px,calc(100vh-48px))] rounded-2xl overflow-hidden bg-[#0B1B33] text-white border border-white/10 shadow-[0_40px_120px_-20px_rgba(11,27,51,0.8)] flex flex-col"
          >
            {/* header */}
            <div className="relative px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-brandblue/40 via-ova/20 to-transparent pointer-events-none" />
              <div className="relative h-10 w-10 rounded-full bg-gradient-to-br from-brandblue to-ova grid place-items-center ring-2 ring-white/20">
                <Sparkle size={16} weight="fill" className="text-white" />
              </div>
              <div className="relative flex-1">
                <div className="font-heading text-base font-bold leading-none">Ava</div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60 mt-1 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" />
                  AI concierge · MedexOVA × OVA
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="concierge-close" className="relative h-8 w-8 grid place-items-center rounded-md hover:bg-white/10">
                <X size={16} />
              </button>
            </div>

            {/* messages */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      m.role === "user"
                        ? "bg-brandblue text-white rounded-br-sm"
                        : "bg-white/[0.06] text-white/90 border border-white/10 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </motion.div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="max-w-[60%] rounded-2xl px-4 py-2.5 text-sm bg-white/[0.06] border border-white/10">
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                      <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                      <span className="h-1.5 w-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                    </span>
                  </div>
                </div>
              )}
              {bookingId && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 rounded-xl border border-brandblue/30 bg-brandblue/10 p-3 text-sm"
                  data-testid="concierge-booking-card"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">Trip submitted</div>
                  <div className="mt-1 font-mono text-xs text-white/70" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    #{bookingId.slice(0, 8)}
                  </div>
                  <Link
                    to={bookingLink?.startsWith("http") ? new URL(bookingLink).pathname : (bookingLink || `/t/${bookingId}`)}
                    className="mt-2 inline-flex items-center gap-1.5 text-brandblue hover:text-white text-xs font-semibold"
                  >
                    Track live <ArrowRight size={12} weight="bold" />
                  </Link>
                </motion.div>
              )}
            </div>

            {/* quick chips */}
            {messages.length <= 1 && !sending && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {[
                  "I need a dialysis ride Mon/Wed/Fri",
                  "Airport pickup for mom tomorrow",
                  "Wheelchair trip to doctor's appointment",
                  "Hospital discharge to skilled nursing",
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => setDraft(q)}
                    className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 transition text-white/80"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* input */}
            <div className="px-3 pb-3 pt-2 border-t border-white/10 bg-black/30">
              <div className="flex items-end gap-2 rounded-xl bg-white/[0.06] border border-white/10 focus-within:border-brandblue/50 px-3 py-2">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                  disabled={!sessionId || sending || !!bookingId}
                  rows={1}
                  placeholder={bookingId ? "Trip submitted — reach out for anything else." : "Ask Ava to book a trip…"}
                  data-testid="concierge-input"
                  className="flex-1 resize-none bg-transparent outline-none text-sm text-white placeholder:text-white/40 max-h-24"
                  style={{ lineHeight: "1.4" }}
                />
                <button
                  onClick={send}
                  disabled={!draft.trim() || sending || !sessionId || !!bookingId}
                  data-testid="concierge-send"
                  className="h-8 w-8 grid place-items-center rounded-lg bg-brandblue hover:bg-brandblue-hover disabled:opacity-40 disabled:cursor-not-allowed transition"
                  aria-label="Send"
                >
                  <PaperPlaneTilt size={14} weight="fill" />
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-white/40">
                <span>Enter to send · Shift+Enter for newline</span>
                <span className="inline-flex items-center gap-1"><Sparkle size={10} /> Claude Sonnet 4.5</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
